import { defineStore } from 'pinia';
import { TrendyolMarketplace } from '@/v2/modules/products/entities/marketplaces/trendyol';
import { useProductEditorStore } from '@/v2/modules/products/stores/product-editor/index';
import { marketplacesEnum } from '@/v2/utils/marketplaces';

function getAttributeValue(attribute, variantId) {
	const isSlicerOrVarianter = attribute.slicer || attribute.varianter;

	if (isSlicerOrVarianter) {
		return attribute.values[variantId];
	} else {
		return attribute.value;
	}
}

export const useTrendyolStore = defineStore('trendyol', {
	state: () => ({
		data: {},
		loadingAttr: false,
		variants: {}
	}),
	getters: {
		attributes: (state) => (linkedMarketplaceId) => {
			if (!linkedMarketplaceId || !state.data[linkedMarketplaceId]) {
				return [];
			}
			return state.data[linkedMarketplaceId].attributes.map((el) => {
				return {
					...el,
					rules: el.required == 1 ? ['required'] : [],
					fetchDataFunction: (params) =>
						TrendyolMarketplace.getAttributeValues(
							params,
							state.data[linkedMarketplaceId].category?.id,
							el?.marketplace_attribute_id,
						),
				};
			});
		},
		formValues(state) {
			const productEditorStore = useProductEditorStore();
			const formValues = {};

			productEditorStore.trendyolLinkedMarketplaces.forEach((m) => {
				if (!state.data[m.id]) return;

				const linkedVariants = {};

				productEditorStore.data.variants.forEach((variant) => {
					const linked_variant_attribute_values = [];
					state.data[m.id].attributes.forEach((attribute) => {
						const value = getAttributeValue(attribute, variant.id);

						linked_variant_attribute_values.push({
							marketplace_attributes_id: attribute.marketplace_attribute_id,
							marketplace_attribute_value_id: !attribute?.allow_custom ? value?.id : undefined,
							marketplace_attribute_value_custom: attribute?.allow_custom ? value : undefined,
						});
					});

					linkedVariants[variant.id] = {
						linked_variant_attribute_values,
						title_ar: state.data[m.id].titles[variant.id],
						description_ar: state.data[m.id].descriptions[variant.id],
						sku: state.data[m.id].sku[variant.id],
						marketplace_category_id: state.data[m.id].category?.id,
						marketplace_brand_id: state.data[m.id].brand?.id,
						linked_variant_images: (state.data[m.id].linked_variant_images[variant.id] || [])
							.filter((el) => el.new)
							.map((item) => ({
								product_variants_image_id: item.id,
								marketplace_id: marketplacesEnum.Trendyol,
								linked_marketplace_id: m.id,
								image_url: item.image,
							})),
						extra_data: {
							productMainId: state.data[m.id].productMainId,
						},
					};
				});

				formValues[m.id] = {
					linked_product: {},
					linked_variant: linkedVariants,
				};
			});

			return formValues;
		},
	},
	actions: {
		async getCategoryAttributes(categoryID, linkedMarketplaceId, populateExistingValues = false) {
			this.loadingAttr = true;
			try {
				const data = await TrendyolMarketplace.getCategoryAttributes(categoryID);
				if (!this.data[linkedMarketplaceId]) {
					this.data[linkedMarketplaceId] = {};
				}
				this.data[linkedMarketplaceId].attributes = data?.data ?? [];
				this.initAttributesValues(linkedMarketplaceId);

				// If populateExistingValues is true, populate attributes from stored linked_variant_attribute_values
				if (populateExistingValues) {
					this.setAttributesValues(linkedMarketplaceId);
				}
			} finally {
				this.loadingAttr = false;
			}
		},
		initAttributesValues(linkedMarketplaceId) {
			if (!this.data[linkedMarketplaceId]) return;

			this.data[linkedMarketplaceId].attributes
				.filter((a) => a.slicer || a.varianter)
				.forEach((attribute) => {
					attribute.values = {};
				});

			this.data[linkedMarketplaceId].attributes
				.filter((a) => !a.slicer && !a.varianter)
				.forEach((attribute) => {
					attribute.value = null;
				});
		},
		setAttributesValues(linkedMarketplaceId) {
			if (!this.data[linkedMarketplaceId] || !this.variants[linkedMarketplaceId]) return;

			this.data[linkedMarketplaceId].attributes
				.filter((a) => a.slicer || a.varianter)
				.forEach((attribute) => {
					const values = {};

					this.variants[linkedMarketplaceId].forEach((variant) => {
						const attributeValues = variant?.linked_variant_attribute_values || [];
						const valObj = attributeValues.find(
							(el) => el.marketplace_attributes_id == attribute.marketplace_attribute_id,
						);

						if (valObj) {
							values[variant.variant_id] =
								valObj[
									attribute.allow_custom == 1
										? 'marketplace_attribute_value_custom'
										: 'marketplace_attribute_value'
								];
						}
					});

					attribute.values = values;
				});

			this.data[linkedMarketplaceId].attributes
				.filter((a) => !a.slicer && !a.varianter)
				.forEach((attribute) => {
					const attributeValues = this.variants[linkedMarketplaceId][0]?.linked_variant_attribute_values || [];
					const valObj = attributeValues.find(
						(el) => el.marketplace_attributes_id == attribute.marketplace_attribute_id,
					);
					if (valObj) {
						attribute.value =
							valObj[
								attribute.allow_custom == 1
									? 'marketplace_attribute_value_custom'
									: 'marketplace_attribute_value'
							];
					}
				});
		},
		async setData(marketplacesData) {
			const productEditorStore = useProductEditorStore();

			marketplacesData.forEach((trendyol) => {
				const linkedProduct = trendyol?.linked_product;
				const linkedMarketplaceId = trendyol?.linked_product?.linked_marketplace_id;
				const linkedVariants = trendyol?.linked_variant;
				const variants = Object.values(linkedVariants).map((el) => el[0]);

				const titles = {};
				const descriptions = {};
				const sku = {};
				const linked_variant_images = {};
				let productMainId = null;

				variants.forEach((el) => {
					titles[el.variant_id] =
						el.translations.find((item) => item.locale == 'ar')?.title ||
						productEditorStore.data.title_ar;
					descriptions[el.variant_id] =
						el.translations.find((item) => item.locale == 'ar')?.description ||
						productEditorStore.data.short_description_ar;
					sku[el.variant_id] = el.sku;
					linked_variant_images[el.variant_id] = el.linked_product_variant_images.map(
						(image) => ({
							...image,
							image: image.path,
						}),
					);
				});
				productMainId = variants[0]?.extra_data?.productMainId || productEditorStore.data.sku;

				this.variants[linkedMarketplaceId] = variants;
				this.data[linkedMarketplaceId] = {
					brand: variants[0]?.marketplace_brand,
					category: variants[0]?.marketplace_category,
					productMainId,
					attributes: [],
					titles,
					descriptions,
					sku,
					linked_variant_images,
				};

				// Pass true to populate existing attribute values after loading category attributes
				this.getCategoryAttributes(variants[0]?.marketplace_category?.id, linkedMarketplaceId, true);
			});
		},
		async submit() {},
	},
});
